import { createToastManager, ToastProvider } from '@curri/components'
import { useCallback, useEffect, useState } from 'react'

import { useAuth } from './auth/AuthProvider.tsx'
import { resolveDocPath } from './docs/manifest.ts'
import { unlistedRouteFor } from './docs/unlisted-routes.ts'
import { GhostProvider } from './ghost/GhostProvider.tsx'
import { Bookings } from './routes/bookings/Bookings.tsx'
import { Credentials } from './routes/credentials/Credentials.tsx'
import { Docs } from './routes/docs/Docs.tsx'
import { Terms } from './routes/docs/Terms.tsx'
import { Landing } from './routes/landing/Landing.tsx'
import { Placeholder } from './routes/Placeholder.tsx'
import { Reference } from './routes/Reference.tsx'
import { Webhooks } from './routes/webhooks/Webhooks.tsx'
import {
  destinationForSection,
  sectionForDocTree,
  sectionForPath,
  UNLISTED_DOCS_SECTION,
  type Destination,
  type Section,
} from './shell/destinations.ts'
import { Rail, RAIL_COLLAPSED_KEY } from './shell/Rail.tsx'
import { SessionControl } from './shell/SessionControl.tsx'
import { SessionToast } from './shell/SessionToast.tsx'

// The portal shell: nav rail and the content sheet it sits behind.
//
// Routing is History-API based and deliberately hand-rolled — three static
// destinations do not justify a router dependency, and the service already
// serves index.html for any unmatched path so deep links work on reload.
//
// The shell owns its own toast manager rather than expecting one from above, so
// mounting `<App />` needs nothing but an `AuthProvider`. It also owns the ghost
// provider (src/ghost) — which partner a Curri admin is reading as is shell-level
// view state, and putting it here rather than in the auth provider is what keeps
// it from ever being mistaken for the session itself.

const useLocationPath = (): [string, (path: string) => void] => {
  const [pathname, setPathname] = useState(window.location.pathname)

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname)
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const navigate = useCallback((to: string) => {
    window.history.pushState({}, '', to)
    // Read the pathname back rather than storing `to`. A restored sign-in target
    // carries the query the visitor left from (`returnTo` is pathname + search),
    // and `sectionForPath` matches on the pathname alone — so storing the raw
    // string resolved `/dashboard/webhooks?status=failed` to no section at all
    // and rendered the landing page under the deep link's own URL. Same read the
    // popstate handler does, so both paths agree on what the state means.
    setPathname(window.location.pathname)
  }, [])

  return [pathname, navigate]
}

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(RAIL_COLLAPSED_KEY) === 'true'
  } catch {
    // Private-mode or blocked storage is not a reason to fail to render.
    return false
  }
}

export const App = () => {
  const { state, restoredPath } = useAuth()
  const [pathname, navigate] = useLocationPath()
  const [collapsed, setCollapsed] = useState(readCollapsed)
  // Created once for the shell's lifetime, so a toast is not dropped by a
  // re-render of the tree that raised it.
  const [toastManager] = useState(() => createToastManager())

  // A completed sign-in redirect lands on `/` — the registered redirect URI —
  // carrying the page the visitor left from. Routing lives here, so the provider
  // hands the path over and this navigates: writing history in the provider moved
  // the address bar without re-rendering, so the URL and the screen disagreed.
  useEffect(() => {
    if (restoredPath && restoredPath !== window.location.pathname) {
      navigate(restoredPath)
    }
  }, [restoredPath, navigate])

  // Documentation resolves first, and by prefix: one section id covers ~40 pages,
  // so `sectionForPath` — which matches whole paths — would send every doc deep
  // link to the landing page.
  //
  // Each tree declares its rail treatment in `sectionForDocTree`: GraphQL and
  // Webhooks have rail entries, while Eclipse and DSP resolve to the unlisted
  // section, which matches no destination and so leaves the rail unselected
  // rather than falsely highlighting Home.
  // The second look through `sectionForPath` covers bare group paths: `/docs`
  // resolves to the group's first section, and that section is a documentation
  // tree (GraphQL) rather than a component route, so the resolver has to be
  // handed the section's path or the bare group would render the placeholder.
  const doc =
    resolveDocPath(pathname) ?? resolveDocPath(sectionForPath(pathname).path)
  const unlistedRoute = doc ? null : unlistedRouteFor(pathname)
  const section = doc
    ? sectionForDocTree(doc.tree.id)
    : unlistedRoute
      ? UNLISTED_DOCS_SECTION
      : sectionForPath(pathname)
  const destination = destinationForSection(section)

  const toggleCollapsed = useCallback(() => {
    setCollapsed(previous => {
      const next = !previous
      try {
        localStorage.setItem(RAIL_COLLAPSED_KEY, String(next))
      } catch {
        // Preference is best-effort; the layout still toggles for this session.
      }
      return next
    })
  }, [])

  const openDestination = useCallback(
    (next: Destination) => navigate(next.sections[0].path),
    [navigate]
  )

  const openSection = useCallback(
    (next: Section) => navigate(next.path),
    [navigate]
  )

  // The landing page and the reference are public; a gated section falls through
  // to the placeholder, which prompts for sign-in rather than rendering an empty
  // screen. Webhooks and Bookings are the exceptions: each renders its own
  // signed-out state (routes/*/SignedOut.tsx), so neither is ever handed to the
  // placeholder.
  const gated =
    section.authenticated &&
    section.id !== 'webhooks' &&
    section.id !== 'bookings' &&
    state.status !== 'authenticated'

  const renderSection = () => {
    if (gated) return <Placeholder section={section} />
    if (doc) {
      return <Docs onNavigate={navigate} page={doc.page} tree={doc.tree} />
    }
    if (unlistedRoute === 'terms') return <Terms onNavigate={navigate} />

    switch (section.id) {
      case 'home':
        return <Landing onNavigate={navigate} />
      case 'reference':
        return <Reference />
      case 'bookings':
        return <Bookings onNavigate={navigate} />
      case 'webhooks':
        return <Webhooks onNavigate={navigate} />
      case 'credentials':
        return <Credentials />
      default:
        return <Placeholder section={section} />
    }
  }

  return (
    // No header bar: the rail carries the brand and the session control, and the
    // sheet starts below its own top margin — the product chrome's layout.
    <ToastProvider toastManager={toastManager}>
    <GhostProvider>
    {/* `#root` is fixed to the viewport (theme/tokens.css), so 100% here is a
        definite height with no percentage chain to collapse — which is what let
        the reference's 13,000px size the document instead of scrolling in place. */}
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <Rail
        activeId={destination.id}
        activeSectionId={section.id}
        collapsed={collapsed}
        footer={<SessionControl collapsed={collapsed} />}
        onSelect={openDestination}
        onSelectSection={openSection}
        onToggleCollapsed={toggleCollapsed}
      />
      {/* The content sheet. White on the shell's grey base, with a hairline and a
          rounded leading corner so it reads as a layer above the nav rather than
          another panel beside it. Flush right and bottom; the top margin is what
          lets the base show through above it. */}
      <main
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          marginTop: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--curri-sheet)',
          borderTop: '1px solid var(--curri-border)',
          borderLeft: '1px solid var(--curri-border)',
          borderTopLeftRadius: 'var(--curri-radius-lg)',
        }}
      >
        {/* Keyed on the section so the fade re-runs per navigation, and so a
            screen never renders with the previous one's mounted state. */}
        <div className="curri-page" key={section.id} style={{ flex: 1, minHeight: 0 }}>
          {renderSection()}
        </div>
      </main>
      {/* Outside the sheet: the viewport is fixed-position chrome, and a sign-in
          failure has to be visible wherever the redirect landed. */}
      <SessionToast />
    </div>
    </GhostProvider>
    </ToastProvider>
  )
}
